import { post } from "@/shared/utils/fetchApi";
import { userDataCache, SessionResponse } from "./UserDataCache";

/**
 * Interface para a sessão do usuário
 */
export interface UserSession {
  user?: {
    id: string;
    email?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

/**
 * Classe de serviço para encapsular todas as operações relacionadas à autenticação
 * Implementa o padrão Facade para simplificar as chamadas à API
 */
export class SessionService {
  /**
   * Obtém a sessão atual do usuário
   * Usa cache para melhorar performance e reduzir chamadas à API
   * @returns Dados da sessão
   */
  static async getSession(): Promise<SessionResponse> {
    try {
      // Usar o cache para obter os dados do usuário
      return await userDataCache.getSession();
    } catch (error) {
      console.error(
        "[SessionService] Erro ao buscar sessão do usuário:",
        error,
      );
      return { data: { session: null }, error };
    }
  }

  /**
   * Obtém o ID do usuário atual
   * @returns ID do usuário ou null se não estiver autenticado
   */
  static async getCurrentUserId(): Promise<string | null> {
    try {
      // Usar o método otimizado do cache para obter apenas o ID
      return await userDataCache.getUserId();
    } catch (error) {
      console.error("[SessionService] Erro ao obter ID do usuário:", error);
      return null;
    }
  }

  /**
   * Invalida o cache de dados do usuário
   * Útil após logout ou alterações no perfil
   */
  static invalidateUserCache(): void {
    userDataCache.invalidateCache();
  }

  /**
   * Força uma atualização dos dados do usuário no cache
   * @returns Dados atualizados da sessão
   */
  static async refreshUserData(): Promise<SessionResponse> {
    return await userDataCache.refreshCache();
  }

  /**
   * Realiza logout do usuário, invalidando o cache e chamando o endpoint de sign-out
   * @returns Objeto com status do sucesso e mensagem opcional
   */
  static async logout(): Promise<{ success: boolean; message?: string }> {
    try {
      userDataCache.invalidateCache();
      // `POST /auth/logout` responde flat (`{ message }`), sem `success`. Sem a
      // normalização, quem lê `response.success` recebia `undefined` e tratava um
      // logout bem-sucedido como falha — é o que o `ResetPassword` faz.
      const raw = await post<{ message?: string }>("/auth/logout");
      return { success: true, message: raw?.message };
    } catch (error) {
      console.error("[SessionService] Erro ao fazer logout:", error);
      return { success: false, message: "Logout failed" };
    }
  }
}
